/**
 * Collectors for the Diagnostics panel: one function per section, each returning report lines.
 * They only read (fetch with no-store, Cache Storage keys, IndexedDB, the viewer's window...);
 * a throw is caught by runSection so one failing source never breaks the panel.
 * Pure formatting lives in format.ts; this file is the browser-facing half.
 */
import { formatReport, compareVersions, describeProbe, errorText, formatBytes, formatDuration, formatLog, formatStamp, formatTime, kv, pickHeaders, runSection, walkProbeNote, type ProbeResult, type Section } from "./format";
import { diagLog } from "./capture";
import { diagState } from "./state";
import { clipLine, pathOnly, redactUrl } from "./redact.ts";
import { deviceGpuLimitMb, probeAstcSupport } from "../walk/memory";
import { fpsOf, textureFormatOf } from "../walk/playerApi";
import { getWalkBlob, listWalks } from "../walk/storage";
import type { WorkerDiag } from "../walk/protocol";

const FETCH_TIMEOUT_MS = 6000;
const WORKER_TIMEOUT_MS = 2000;

const sleep = <T>(ms: number, value: T): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(value), ms));

function appUrl(path: string, bust = true): URL {
  const url = new URL(path, document.baseURI);
  if (bust) url.searchParams.set("diag", String(Date.now()));
  return url;
}

type Fetched = { ok: ProbeResult; headers: string; text: string | null } | { error: string; ms: number };

/** One request with no-store and a timeout. Reads the body only when `wantText`. */
async function fetchOnce(url: URL, method: "HEAD" | "GET", wantText = false): Promise<Fetched> {
  const started = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { method, cache: "no-store", signal: controller.signal });
    const contentType = response.headers.get("content-type");
    let text: string | null = null;
    if (wantText || (method === "GET" && response.status >= 400 && (contentType ?? "").startsWith("text/"))) {
      text = (await response.text()).slice(0, 200);
    }
    const ms = performance.now() - started;
    return {
      ok: { status: response.status, contentType, redirected: response.redirected, finalUrl: redactUrl(response.url), ms, snippet: method === "GET" && response.status >= 400 ? (text ?? undefined) : undefined },
      headers: pickHeaders(response.headers),
      text,
    };
  } catch (err) {
    return { error: controller.signal.aborted ? `no answer in ${FETCH_TIMEOUT_MS / 1000} s` : errorText(err), ms: performance.now() - started };
  } finally {
    clearTimeout(timer);
  }
}

function timeLimited<T>(promise: Promise<T>, ms: number): Promise<T | "timeout"> {
  return Promise.race([promise, sleep(ms, "timeout" as const)]);
}

/** Query parameters of the page URL: values only for the Player's own switches. */
function paramLines(search: string): string {
  const params = new URLSearchParams(search);
  const known = /^(kiosk|fps|diagnostics|lm_format|tex_format|slide|shared|ae\w*|bloom\w*|mb\w*|zoom|lens\w*)$/;
  const parts: string[] = [];
  for (const [name, value] of params) parts.push(known.test(name) ? `${name}=${clipLine(value, 20)}` : name);
  return parts.length ? parts.join(" ") : "(none)";
}

function displayModes(): string {
  const found: string[] = [];
  for (const mode of ["fullscreen", "standalone", "minimal-ui", "browser"]) {
    try {
      if (matchMedia(`(display-mode: ${mode})`).matches) found.push(mode);
    } catch {
      // ignore
    }
  }
  const ios = (navigator as unknown as { standalone?: boolean }).standalone;
  return `${found.join("+") || "unknown"}${ios !== undefined ? ` (navigator.standalone=${ios})` : ""}`;
}

async function appSection(): Promise<string[]> {
  const lines = kv([
    ["running version", `${__APP_VERSION__}${import.meta.env.DEV ? " (dev build)" : ""}`],
    ["build date", __APP_RELEASED_AT__],
    ["location", redactUrl(location.href)],
    ["origin", location.origin],
    ["protocol", location.protocol],
    ["isSecureContext", String(isSecureContext)],
    ["display-mode", displayModes()],
    ["url params", paramLines(location.search)],
    ["visibility", `${document.visibilityState}, readyState ${document.readyState}`],
    ["page age", formatDuration(performance.now())],
  ]);
  const fetched = await fetchOnce(appUrl("version.json"), "GET", true);
  if ("error" in fetched) {
    lines.push(`deployed version.json : unreadable (${fetched.error})`);
    return lines;
  }
  let deployed: string | null = null;
  let releasedAt = "n/a";
  try {
    const data = JSON.parse(fetched.text ?? "") as { version?: unknown; releasedAt?: unknown };
    if (typeof data.version === "string") deployed = data.version;
    if (typeof data.releasedAt === "string") releasedAt = data.releasedAt;
  } catch {
    // not JSON: a host answering unknown URLs with index.html
  }
  lines.push(
    ...kv([
      ["deployed version", deployed ?? `unreadable (status ${fetched.ok.status}, ${fetched.ok.contentType ?? "no content-type"})`],
      ["deployed build date", releasedAt],
      ["update available?", compareVersions(__APP_VERSION__, deployed)],
    ]),
  );
  return lines;
}

async function serverSection(): Promise<string[]> {
  const lines = kv([
    ["navigator.onLine", String(navigator.onLine)],
    ["connection", connectionText()],
    ["note", "each request below has a cache-busting ?diag= parameter so the service worker's precache does not answer"],
  ]);
  const targets: [string, string][] = [
    ["/", ""],
    ["/sw.js", "sw.js"],
    ["/version.json", "version.json"],
    ["/manifest.webmanifest", "manifest.webmanifest"],
  ];
  const results = await Promise.all(
    targets.map(async ([label, path]) => {
      let fetched = await fetchOnce(appUrl(path), "HEAD");
      let method = "HEAD";
      if (!("error" in fetched) && (fetched.ok.status === 405 || fetched.ok.status === 501)) {
        fetched = await fetchOnce(appUrl(path), "GET");
        method = "GET";
      }
      return { label, method, fetched };
    }),
  );
  for (const { label, method, fetched } of results) {
    lines.push("", `${method} ${label}`);
    lines.push(...("error" in fetched ? describeProbe(fetched, "") : describeProbe(fetched.ok, fetched.headers)));
  }
  const probes: [string, string][] = [
    ["/walk/__probe__/index.html  (note: answered by the service worker when it controls this page)", "walk/__probe__/index.html"],
    ["/%77alk/__probe__/index.html  (same path percent-encoded: skips the service worker, shows what the SERVER answers)", "%77alk/__probe__/index.html"],
  ];
  for (const [label, path] of probes) {
    const fetched = await fetchOnce(appUrl(path), "GET");
    lines.push("", `GET ${label}`);
    lines.push(...("error" in fetched ? describeProbe(fetched, "") : describeProbe(fetched.ok, fetched.headers)));
    const note = walkProbeNote("error" in fetched ? fetched : fetched.ok);
    if (note) lines.push(`  meaning: ${note}`);
  }
  return lines;
}

function connectionText(): string {
  const c = (navigator as unknown as { connection?: { effectiveType?: string; rtt?: number; downlink?: number; saveData?: boolean } }).connection;
  if (!c) return "not exposed by this browser";
  return `effectiveType=${c.effectiveType ?? "n/a"} rtt=${c.rtt ?? "n/a"} ms downlink=${c.downlink ?? "n/a"} Mbps saveData=${c.saveData ?? "n/a"}`;
}

export async function askWorker(worker: ServiceWorker): Promise<WorkerDiag | string> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve("no answer within 2 s"), WORKER_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent<{ ok?: boolean; diag?: WorkerDiag; diagError?: string }>) => {
      clearTimeout(timer);
      if (event.data?.diag) resolve(event.data.diag);
      else if (event.data?.diagError) resolve(`the worker failed to answer: ${event.data.diagError}`);
      else resolve("SW does not answer diagnostics (old version)");
    };
    try {
      worker.postMessage({ type: "diag" }, [channel.port2]);
    } catch (err) {
      clearTimeout(timer);
      resolve(`could not message the worker: ${errorText(err)}`);
    }
  });
}

function workerLine(worker: ServiceWorker | null): string {
  return worker ? `${worker.state} ${pathOnly(worker.scriptURL)}` : "none";
}

function describeWorkerDiag(label: string, answer: WorkerDiag | string, now: number): string[] {
  if (typeof answer === "string") return [`${label}: ${answer}`];
  const lines = kv([
    [`${label} version`, `${answer.version}${answer.version !== __APP_VERSION__ ? `   <-- DIFFERS from the page (${__APP_VERSION__})` : ""}`],
    [`${label} started`, `${formatStamp(answer.startedAt, now)} (a recent start means the browser stopped and restarted the worker)`],
    [`${label} clients`, `${answer.clients.controlled} controlled, ${answer.clients.all} visible`],
    [`${label} walk sessions`, answer.sessions.length ? answer.sessions.join(", ") : "none in memory"],
  ]);
  lines.push(`${label} last ${answer.fetches.length} walk/ fetches (oldest first; times UTC):`);
  if (!answer.fetches.length) lines.push("  (none yet)");
  for (const f of answer.fetches) {
    lines.push(`  ${formatTime(f.t)}  ${f.result.padEnd(5)} ${f.source.padEnd(15)} ${clipLine(f.path, 120)}`);
  }
  return lines;
}

async function serviceWorkerSection(): Promise<string[]> {
  if (!("serviceWorker" in navigator)) return ["service workers are not available in this browser"];
  const sw = navigator.serviceWorker;
  const now = Date.now();
  const registrations = await sw.getRegistrations();
  const lines: string[] = [`registrations: ${registrations.length}`];
  registrations.forEach((r, i) => {
    lines.push(
      ...kv([
        [`#${i + 1} scope`, redactUrl(r.scope)],
        ["  active", workerLine(r.active)],
        ["  waiting", workerLine(r.waiting)],
        ["  installing", workerLine(r.installing)],
        ["  updateViaCache", r.updateViaCache],
      ]),
    );
  });
  const ready = await timeLimited(sw.ready.then((r) => `resolved (scope ${redactUrl(r.scope)})`), 1500);
  lines.push(
    ...kv([
      ["controller", sw.controller ? workerLine(sw.controller) : "NULL (this page is not controlled by a service worker)"],
      ["serviceWorker.ready", ready === "timeout" ? "NOT resolved within 1.5 s (no active worker yet?)" : ready],
    ]),
  );
  const asked = new Set<ServiceWorker>();
  const targets: [string, ServiceWorker | null | undefined][] = [
    ["controller", sw.controller],
    ["active", registrations[0]?.active],
    ["waiting", registrations[0]?.waiting],
  ];
  for (const [label, worker] of targets) {
    if (!worker || asked.has(worker)) continue;
    asked.add(worker);
    lines.push("", ...describeWorkerDiag(label, await askWorker(worker), now));
  }
  if (!asked.size) lines.push("", "no worker to ask");
  return lines;
}

interface CacheScan {
  name: string;
  count: number;
}

async function storageSection(): Promise<string[]> {
  const lines: string[] = [];
  // Cache Storage.
  if ("caches" in window) {
    const names = await caches.keys();
    const scans: CacheScan[] = [];
    const found = { viewerIndex: false, viewerMain: false, shell: false };
    for (const name of names) {
      const keys = await (await caches.open(name)).keys();
      scans.push({ name, count: keys.length });
      for (const request of keys) {
        const path = new URL(request.url).pathname;
        if (path.endsWith("/viewer/index.html")) found.viewerIndex = true;
        if (path.endsWith("/viewer/app/main.js")) found.viewerMain = true;
        if (path.endsWith("/index.html") && !path.includes("/viewer/")) found.shell = true;
      }
    }
    lines.push(`Cache Storage: ${names.length} cache(s)`);
    for (const scan of scans) lines.push(`  ${scan.name} : ${scan.count} entries`);
    lines.push(`  precache has app index.html       : ${found.shell}`, `  precache has viewer/index.html    : ${found.viewerIndex}`, `  precache has viewer/app/main.js   : ${found.viewerMain}`);
  } else {
    lines.push("Cache Storage: not available");
  }
  // IndexedDB: saved walks.
  lines.push("");
  const dbs = (indexedDB as unknown as { databases?: () => Promise<{ name?: string; version?: number }[]> }).databases;
  if (dbs) lines.push(`IndexedDB databases: ${(await dbs.call(indexedDB)).map((d) => `${d.name} v${d.version}`).join(", ") || "none"}`);
  const walks = await listWalks();
  lines.push(`saved walks: ${walks.length}`);
  for (const walk of walks.slice(0, 20)) {
    let readable: string;
    try {
      const blob = await getWalkBlob(walk.id);
      if (!blob) readable = "NO (file record missing)";
      else {
        const head = await blob.slice(0, 16).arrayBuffer();
        readable = head.byteLength ? `yes (${formatBytes(blob.size)} blob)` : "NO (empty read)";
      }
    } catch (err) {
      readable = `NO (${errorText(err)})`;
    }
    lines.push(
      `  ${walk.id.slice(0, 8)}  "${clipLine(walk.name, 60)}"  size ${formatBytes(walk.fileSize)}  preset ${walk.preset ?? "n/a"}  gpuMb ${walk.gpuMb ?? "n/a"}  gpuMbUncompressed ${walk.gpuMbUncompressed ?? "n/a"}  lastOpened ${walk.lastOpenedAt ? new Date(walk.lastOpenedAt).toISOString() : "never"}  readable ${readable}`,
    );
  }
  if (walks.length > 20) lines.push(`  ... ${walks.length - 20} more not listed`);
  // localStorage: Player keys only (values of anything else are not read).
  lines.push("");
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i) ?? "");
    const mine = keys.filter((k) => k.startsWith("walk-player")).sort();
    lines.push(`localStorage: ${keys.length} key(s), ${mine.length} of them the Player's`);
    for (const key of mine) {
      const raw = localStorage.getItem(key) ?? "";
      lines.push(`  ${key} = ${key === "walk-player.diaglog" ? `(event log, ${raw.length} chars)` : clipLine(raw, 300)}`);
    }
  } catch (err) {
    lines.push(`localStorage: unavailable (${errorText(err)})`);
  }
  // Quota.
  lines.push("");
  if (navigator.storage?.estimate) {
    const estimate = await navigator.storage.estimate();
    lines.push(`storage.estimate: usage ${formatBytes(estimate.usage)} of quota ${formatBytes(estimate.quota)}`);
  } else {
    lines.push("storage.estimate: not available");
  }
  lines.push(`storage.persisted: ${navigator.storage?.persisted ? String(await navigator.storage.persisted()) : "not available"}`);
  return lines;
}

function walkSection(): string[] {
  const lines: string[] = [];
  let flag = "none";
  try {
    flag = localStorage.getItem("walk-player.loading") ?? "none (no walk is mid-load)";
  } catch {
    flag = "unreadable";
  }
  const now = Date.now();
  const walk = diagState.walk;
  const frame = diagState.frame;
  lines.push(`loading flag (walk-player.loading): ${clipLine(flag, 300)}`);
  if (!walk) {
    lines.push("no walk has been opened since this page started");
    return lines;
  }
  lines.push(
    ...kv([
      ["walk", `"${clipLine(walk.name, 60)}" id ${walk.id}`],
      ["state", walk.closedAt ? `closed ${formatDuration(now - walk.closedAt)} ago` : "OPEN"],
      ["gpuMb / preset", `${walk.gpuMb ?? "n/a"} / ${walk.preset ?? "n/a"}`],
      ["open started", formatStamp(walk.openStartedAt, now)],
      ["time since open", formatDuration(((walk.closedAt ?? now) - walk.openStartedAt))],
      ["handed to viewer", walk.shownAt ? `${formatDuration(walk.shownAt - walk.openStartedAt)} after open started` : "not yet"],
    ]),
  );
  if (!frame) {
    lines.push("iframe: none created");
    return lines;
  }
  lines.push(
    ...kv([
      ["iframe.src", pathOnly(frame.src)],
      ["iframe in page", frame.closed ? "no (removed)" : "yes"],
      ["load event", frame.loadedAt ? `fired ${formatDuration(frame.loadedAt - frame.createdAt)} after creation` : "NOT fired yet"],
      ["at load", frame.loadNote],
    ]),
  );
  if (frame.closed) return lines;
  const win = frame.el.contentWindow;
  let doc: Document | null = null;
  try {
    doc = frame.el.contentDocument;
  } catch {
    doc = null;
  }
  lines.push(`same-origin reachable now: ${doc ? "yes" : "NO (cross-origin or a browser error page)"}`);
  if (doc) {
    lines.push(`viewer loading text: ${clipLine(doc.querySelector("#file")?.textContent ?? "(no #file element: not the viewer page?)", 160)}`);
    const wb = (win as unknown as { walkbake?: { ready?: boolean; error?: unknown; stats?: () => Record<string, unknown> } } | null)?.walkbake;
    lines.push(`walkbake: ${wb ? `ready=${String(wb.ready)} error=${wb.error ? clipLine(String(wb.error), 200) : "null"}` : "not defined (viewer script has not run)"}`);
    lines.push(`walkPlayer.textureFormat(): ${JSON.stringify(textureFormatOf(win))}`, `walkPlayer.fps(): ${fpsOf(win)}`);
    try {
      const s = wb?.stats?.() as { drawCalls?: number; triangles?: number; textures?: number; geometries?: number; pixelRatio?: number; canvas?: number[]; heap?: { now?: number; peakLoading?: number } | null } | undefined;
      if (s) {
        lines.push(
          `viewer stats: drawCalls ${s.drawCalls} triangles ${s.triangles} textures ${s.textures} geometries ${s.geometries} pixelRatio ${s.pixelRatio?.toFixed?.(2)} canvas ${s.canvas?.join("x")}` +
            (s.heap ? ` jsHeap ${formatBytes(s.heap.now)} (peak while loading ${formatBytes(s.heap.peakLoading)})` : ""),
        );
      }
    } catch (err) {
      lines.push(`viewer stats: failed (${errorText(err)})`);
    }
  }
  return lines;
}

let gpuInfo: string[] | null = null;

function webglLines(): string[] {
  if (gpuInfo) return gpuInfo;
  const lines: string[] = [];
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2");
    if (!gl) {
      lines.push("WebGL2            : NOT available");
    } else {
      const debug = gl.getExtension("WEBGL_debug_renderer_info");
      lines.push(
        `WebGL2 renderer   : ${debug ? clipLine(String(gl.getParameter(debug.UNMASKED_RENDERER_WEBGL)), 120) : "hidden by the browser"}`,
        `WebGL2 vendor     : ${debug ? clipLine(String(gl.getParameter(debug.UNMASKED_VENDOR_WEBGL)), 120) : "hidden by the browser"}`,
        `max texture size  : ${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`,
        `max renderbuffer  : ${gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)}`,
      );
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    }
  } catch (err) {
    lines.push(`WebGL2            : probe failed (${errorText(err)})`);
  }
  gpuInfo = lines;
  return lines;
}

async function deviceSection(): Promise<string[]> {
  const nav = navigator as unknown as {
    deviceMemory?: number;
    userAgentData?: { brands?: { brand: string; version: string }[]; platform?: string; mobile?: boolean; getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>> };
  };
  const lines = kv([
    ["userAgent", navigator.userAgent],
    ["screen", `${screen.width}x${screen.height}, window ${innerWidth}x${innerHeight}, ${screen.orientation?.type ?? "orientation n/a"}`],
    ["devicePixelRatio", devicePixelRatio],
    ["deviceMemory (GB)", nav.deviceMemory],
    ["hardwareConcurrency", navigator.hardwareConcurrency],
    ["maxTouchPoints", navigator.maxTouchPoints],
    ["gpu limit used by Player", `${deviceGpuLimitMb(navigator.userAgent, navigator.maxTouchPoints)} MB`],
  ]);
  const uach = nav.userAgentData;
  if (uach) {
    let high: Record<string, unknown> | "timeout" = {};
    try {
      high = (await timeLimited(uach.getHighEntropyValues?.(["model", "platformVersion"]) ?? Promise.resolve({}), 1000)) as Record<string, unknown> | "timeout";
    } catch {
      high = {};
    }
    const extra = high === "timeout" ? {} : high;
    lines.push(
      ...kv([
        ["UA-CH brands", uach.brands?.map((b) => `${b.brand} ${b.version}`).join(", ")],
        ["UA-CH platform", `${uach.platform ?? "n/a"} ${String(extra.platformVersion ?? "")}`.trim()],
        ["UA-CH model", String(extra.model ?? "") || "n/a"],
        ["UA-CH mobile", uach.mobile],
      ]),
    );
  } else {
    lines.push("UA-CH             : not available");
  }
  lines.push(...webglLines());
  const astc = probeAstcSupport();
  lines.push(`ASTC profiles     : LDR ${astc.ldr}, HDR ${astc.hdr}`);
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  lines.push(mem ? `performance.memory : used ${formatBytes(mem.usedJSHeapSize)}, total ${formatBytes(mem.totalJSHeapSize)}, limit ${formatBytes(mem.jsHeapSizeLimit)}` : "performance.memory : not exposed by this browser");
  return lines;
}

function eventsSection(): string[] {
  const log = diagLog();
  if (!log) return ["event log is not running"];
  return formatLog(log.entries(), log.sessionId, Date.now());
}

export const SECTION_TITLES = [
  "App",
  "Server",
  "Service worker",
  "Storage",
  "Current / last walk",
  "Device",
  "Events and errors",
] as const;

const COLLECTORS: (() => Promise<string[]> | string[])[] = [appSection, serverSection, serviceWorkerSection, storageSection, walkSection, deviceSection, eventsSection];

/** Collects every section in parallel; `onSection` fires as each one finishes (the panel fills in progressively). */
export async function collectSections(onSection: (index: number, section: Section) => void): Promise<Section[]> {
  const sections: Section[] = [];
  await Promise.all(
    COLLECTORS.map(async (collect, index) => {
      const section = await runSection(SECTION_TITLES[index] ?? "?", collect, 20_000);
      sections[index] = section;
      onSection(index, section);
    }),
  );
  return sections;
}

/** The whole report as text (what Copy report copies), for callers without the panel. */
export async function buildReport(): Promise<string> {
  const sections = await collectSections(() => undefined);
  return formatReport({ generatedAt: Date.now(), version: __APP_VERSION__ }, sections);
}

/** "0.0.30", "no controller", "SW does not answer diagnostics (old version)"... of the worker controlling this page. */
export async function workerVersionText(): Promise<string> {
  const controller = "serviceWorker" in navigator ? navigator.serviceWorker.controller : null;
  if (!controller) return "no service worker controls this page";
  const answer = await askWorker(controller);
  return typeof answer === "string" ? answer : answer.version;
}

/** The event log as a text file's content: this device's last entries, previous sessions included. */
export function eventLogText(): string {
  const log = diagLog();
  const head = ["WALK PLAYER EVENT LOG", `app       : v${__APP_VERSION__}`, `exported  : ${new Date().toISOString()}`, ""];
  return head.concat(log ? formatLog(log.entries(), log.sessionId, Date.now()) : ["event log is not running"]).join("\n") + "\n";
}
